/**
 * The Desktop section of the learner Settings page: Pomodoro lengths,
 * presence thresholds and sources, and launch at login, all held by the
 * desktop shell in its settings.json.
 *
 * Renders nothing outside the desktop app, or in a shell too old to expose
 * its settings. The minute fields save together with a button, since a
 * half-typed number is not a choice; the switches save as they flip, like
 * every other switch on this page.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";

import { Switch } from "apps/user/common/components/fields/inputs";
import { Button } from "components/ui";
import { useTranslation } from "lib/i18n/use-translation";

import {
  getDesktopBridge,
  type DesktopPomodoroSettings,
  type DesktopSettings,
  type DesktopSettingsPatch,
} from "../bridge";
import {
  FIELD_BOUNDS,
  parseMinutes,
  PRESENCE_FIELDS,
  TIMER_FIELDS,
  type MinuteField,
} from "./fields";
import { useDesktopSettings } from "./useDesktopSettings";

import styles from "./DesktopSettingsSection.module.scss";

type Draft = Record<MinuteField, string>;

function draftFrom(pomodoro: DesktopPomodoroSettings): Draft {
  return Object.fromEntries(
    Object.entries(pomodoro).map(([field, value]) => [field, String(value)])
  ) as Draft;
}

const DesktopSettingsSection = () => {
  const { t } = useTranslation(["settings"]);
  const desktop = useMemo(() => getDesktopBridge(), []);
  const bridge = desktop?.settings ?? null;
  const { settings, error, saving, save } = useDesktopSettings(bridge);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saved, setSaved] = useState(false);

  // The shell's copy is the truth, so a field follows it when its saved
  // value changes, after a save or a change made from the tray. A field whose
  // saved value did not change keeps what the learner is typing, so flipping
  // a switch does not throw away an unsaved length.
  const previous = useRef<DesktopPomodoroSettings | null>(null);
  useEffect(() => {
    if (settings === null) return;
    const before = previous.current;
    previous.current = settings.pomodoro;
    setDraft((current) => {
      const fresh = draftFrom(settings.pomodoro);
      if (current === null || before === null) return fresh;
      const merged = { ...current };
      for (const field of Object.keys(fresh) as MinuteField[]) {
        if (settings.pomodoro[field] !== before[field]) {
          merged[field] = fresh[field];
        }
      }
      return merged;
    });
  }, [settings]);

  if (desktop === null || bridge === null) return null;

  const title = (
    <h1 className={styles["title"]}>{t("settings:desktop.sectionTitle")}</h1>
  );

  if (settings === null || draft === null) {
    return (
      <div className={styles["desktop-settings"]}>
        {title}
        {error !== null && <p className={styles["error-message"]}>{error}</p>}
      </div>
    );
  }

  const invalid = (field: MinuteField) =>
    parseMinutes(field, draft[field]) === null;
  const changes: Partial<DesktopPomodoroSettings> = {};
  for (const field of [...TIMER_FIELDS, ...PRESENCE_FIELDS]) {
    const value = parseMinutes(field, draft[field]);
    if (value !== null && value !== settings.pomodoro[field]) {
      changes[field] = value;
    }
  }
  const hasInvalid = [...TIMER_FIELDS, ...PRESENCE_FIELDS].some(invalid);
  const canSave = !hasInvalid && Object.keys(changes).length > 0 && !saving;

  const submit = async (patch: DesktopSettingsPatch) => {
    setSaved(false);
    setSaved(await save(patch));
  };

  const renderField = (field: MinuteField) => {
    const inputId = `desktop-setting-${field}`;
    const errorId = `${inputId}-error`;
    const isInvalid = invalid(field);
    return (
      <div key={field} className={styles["field"]}>
        <div className={styles["field-row"]}>
          <label htmlFor={inputId} className={styles["field-label"]}>
            {t(`settings:desktop.fields.${field}`)}
          </label>
          <input
            id={inputId}
            type="number"
            inputMode="numeric"
            step={1}
            min={FIELD_BOUNDS[field].min}
            max={FIELD_BOUNDS[field].max}
            value={draft[field]}
            aria-invalid={isInvalid}
            aria-describedby={isInvalid ? errorId : undefined}
            className={
              styles[isInvalid ? "field-input-invalid" : "field-input"]
            }
            onChange={(event) => {
              setSaved(false);
              setDraft({ ...draft, [field]: event.target.value });
            }}
          />
        </div>
        {isInvalid && (
          <p id={errorId} className={styles["field-error"]}>
            {t("settings:desktop.rangeError", FIELD_BOUNDS[field])}
          </p>
        )}
      </div>
    );
  };

  const provider: keyof DesktopSettings["presenceProviders"] | null =
    desktop.platform === "darwin"
      ? "calendar"
      : desktop.platform === "win32"
      ? "camera"
      : null;

  return (
    <div className={styles["desktop-settings"]}>
      {title}

      <section className={styles["group"]}>
        <h2 className={styles["group-title"]}>
          {t("settings:desktop.timerTitle")}
        </h2>
        {TIMER_FIELDS.map(renderField)}
      </section>

      <section className={styles["group"]}>
        <h2 className={styles["group-title"]}>
          {t("settings:desktop.presenceTitle")}
        </h2>
        {PRESENCE_FIELDS.map(renderField)}
      </section>

      <div className={styles["actions"]}>
        <Button
          variant="primary"
          size="small"
          disabled={!canSave}
          loading={saving}
          onClick={() => submit({ pomodoro: changes })}
        >
          {t("settings:desktop.save")}
        </Button>
        {saved && (
          <p className={styles["saved"]} role="status">
            {t("settings:desktop.saved")}
          </p>
        )}
      </div>

      <section className={styles["group"]}>
        {provider !== null && (
          <Switch
            id={provider}
            label={t(`settings:desktop.providers.${provider}`)}
            checked={settings.presenceProviders[provider]}
            disabled={saving}
            onChange={(_id: string, checked: boolean) =>
              submit({ presenceProviders: { [provider]: checked } })
            }
          />
        )}
        <Switch
          id="launchAtLogin"
          label={t("settings:desktop.launchAtLogin")}
          checked={settings.launchAtLogin}
          disabled={saving}
          onChange={(_id: string, checked: boolean) =>
            submit({ launchAtLogin: checked })
          }
        />
      </section>

      {error !== null && <p className={styles["error-message"]}>{error}</p>}
    </div>
  );
};

export default DesktopSettingsSection;
